import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertTriangle,
  Clock,
  FileCheck,
  UserX,
  XCircle,
  Bell,
  Loader2,
  ChevronRight,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const AdminAlerts = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({});

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAlerts();
      setAlerts(response.data.alerts || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error loading alerts:', error);
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'kyc_pending': return <FileCheck className="w-4 h-4" />;
      case 'pro_validation': return <Clock className="w-4 h-4" />;
      case 'inactive_users': return <UserX className="w-4 h-4" />;
      case 'high_cancellation': return <XCircle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'info': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'review_kyc': return 'Voir KYC';
      case 'validate_professional': return 'Valider';
      case 'view_inactive': return 'Voir liste';
      case 'review_professional': return 'Voir profil';
      default: return 'Voir';
    }
  };

  const handleAction = (alert) => {
    // Navigate to appropriate section based on action type
    if (onNavigate) {
      switch (alert.action) {
        case 'review_kyc':
          onNavigate('kyc');
          break;
        case 'validate_professional':
          onNavigate('validations');
          break;
        case 'view_inactive':
        case 'review_professional':
          onNavigate('users');
          break;
        default:
          break;
      }
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-slate-300 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const totalAlerts = alerts.length;

  return (
    <Card className="border-0 shadow-sm" data-testid="admin-alerts">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              totalAlerts > 0 ? 'bg-amber-100' : 'bg-emerald-100'
            }`}>
              {totalAlerts > 0 ? (
                <Bell className="w-4 h-4 text-amber-600" />
              ) : (
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              )}
            </div>
            <div>
              <CardTitle className="text-base">
                Alertes
                {totalAlerts > 0 && (
                  <Badge className="ml-2 bg-amber-100 text-amber-700">{totalAlerts}</Badge>
                )}
              </CardTitle>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={loadAlerts} className="h-8 w-8 p-0">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {totalAlerts === 0 ? (
          <div className="text-center py-6 text-slate-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Tout est à jour !</p>
            <p className="text-xs mt-1">Aucune alerte en ce moment</p>
          </div>
        ) : (
          <>
            {/* Summary badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {summary.kyc_pending > 0 && (
                <Badge className="bg-amber-100 text-amber-700">
                  <FileCheck className="w-3 h-3 mr-1" />
                  {summary.kyc_pending} KYC
                </Badge>
              )}
              {summary.pro_validation > 0 && (
                <Badge className="bg-blue-100 text-blue-700">
                  <Clock className="w-3 h-3 mr-1" />
                  {summary.pro_validation} Validations
                </Badge>
              )}
              {summary.high_cancellation > 0 && (
                <Badge className="bg-red-100 text-red-700">
                  <XCircle className="w-3 h-3 mr-1" />
                  {summary.high_cancellation} Annulations
                </Badge>
              )}
            </div>

            {/* Alert list */}
            <ScrollArea className="h-[280px] pr-2">
              <div className="space-y-2">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${getAlertColor(alert.severity)} cursor-pointer hover:opacity-80 transition-opacity`}
                    onClick={() => handleAction(alert)}
                    data-testid={`alert-${index}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <div className="mt-0.5 flex-shrink-0">
                          {getAlertIcon(alert.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{alert.title}</p>
                          <p className="text-xs opacity-80 truncate">{alert.message}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAction(alert);
                        }}
                      >
                        {getActionLabel(alert.action)}
                        <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAlerts;
